import { Link } from '@tanstack/react-router'
import { Trophy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AvailabilityBadge, ConfiguredLimitBadge, ServiceStatusBadge } from '@/shared/components/cse'
import { CatalogCardBody, CatalogCardFooter } from '@/shared/components/CatalogCardLayout'
import { Button } from '@/shared/components/ui/button'
import { formatIsoDateDisplay } from '@/shared/lib/datetime'
import type { LotteryDraw } from '@/features/lottery/schemas/lottery.schema'

export function HorizonDrawCard({ draw }: { draw: LotteryDraw }) {
  const { t } = useTranslation()
  const detailTo = `/lottery/draws/${encodeURIComponent(draw.id)}`

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[var(--brand-radius-card)] border border-[var(--brand-border-soft)] bg-[var(--brand-background)] shadow-[var(--brand-shadow-1)]">
      <div className="flex items-center justify-center bg-[var(--brand-surface-strong)] py-8 text-[var(--brand-primary)]">
        <Trophy className="size-10" aria-hidden />
      </div>
      <CatalogCardBody>
        <h3 className="m-0 text-base font-semibold leading-snug text-[var(--brand-text-primary)]">
          <Link
            to={detailTo}
            className="line-clamp-2 text-inherit no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]"
          >
            {draw.title}
          </Link>
        </h3>
        <p className="text-sm text-[var(--brand-text-secondary)]">
          {draw.scheduled_at
            ? t('lottery.list.scheduled_at', { date: formatIsoDateDisplay(draw.scheduled_at) })
            : t('lottery.list.no_date')}
        </p>
        <div className="flex flex-wrap gap-2">
          <ServiceStatusBadge
            label={t(`lottery.status.${draw.status}`, { defaultValue: draw.status })}
            tone={draw.status === 'scheduled' ? 'open' : 'neutral'}
          />
          <AvailabilityBadge
            label={draw.current_user_participates ? t('lottery.list.already_participating') : t('lottery.list.open_to_participate')}
            available={!draw.current_user_participates}
          />
          <ConfiguredLimitBadge
            label={
              draw.draw_kind === 'tombola' && draw.tombola?.participation_mode === 'paid'
                ? t('lottery.list.tickets_in_play', { count: draw.participations_count })
                : t('lottery.list.participants', { count: draw.participations_count })
            }
          />
        </div>
        <CatalogCardFooter className="border-[var(--brand-border-soft)]">
          <Button size="sm" variant="outline" asChild className="w-full">
            <Link to={detailTo} aria-label={t('lottery.list.view_aria', { title: draw.title })}>
              {t('lottery.list.view')}
            </Link>
          </Button>
        </CatalogCardFooter>
      </CatalogCardBody>
    </article>
  )
}
